import { buildEvolutionSummary, buildGoalsProjection, buildPerformanceAlerts } from './evolutionAnalytics.js';
import { generateId, todayISO } from './utils.js';

const byDateDesc = (a, b) => String(b.data || b.date || '').localeCompare(String(a.data || a.date || ''));

function schoolOf(data, student) {
  return (data.schools || []).find((school) => school.id === student?.schoolId) || {};
}

function peiOf(data, student) {
  return (data.peis || []).find((item) => item.studentId === student.id);
}

function consentOf(data, student) {
  return (data.consents || []).find((item) => item.studentId === student.id && item.ativo);
}

function logsOf(data, student) {
  return (data.logs || []).filter((item) => item.studentId === student.id).sort(byDateDesc);
}

/**
 * Monta o modelo consolidado de relatório de um único estudante, reunindo
 * evolução, alertas de acompanhamento, projeção das metas do PEI/PAEE,
 * atendimentos e consentimento. Usado tanto pela UI quanto pelos exportadores
 * de PDF/Excel, para individual e blocos automáticos.
 */
export function buildStudentReport(data, student) {
  const summary = buildEvolutionSummary(data, student.id);
  const alerts = buildPerformanceAlerts(summary);
  const pei = peiOf(data, student);
  const goals = buildGoalsProjection(pei?.metas || [], summary);
  return {
    student,
    school: schoolOf(data, student),
    summary,
    alerts,
    pei,
    goals,
    logs: logsOf(data, student),
    consent: consentOf(data, student),
    geradoEm: new Date().toISOString(),
  };
}

/**
 * Monta um bloco de relatórios para um grupo de estudantes (turma, escola ou
 * seleção manual), com médias agregadas por habilidade e indicadores de
 * atenção do grupo, além do relatório individual de cada estudante.
 */
export function buildReportBlock(data, students = [], { nome = 'Bloco de relatórios' } = {}) {
  const reports = students.map((student) => buildStudentReport(data, student));
  const skillTotals = {};
  for (const report of reports) {
    for (const [skill, value] of Object.entries(report.summary.current.scores)) {
      (skillTotals[skill] ||= []).push(value);
    }
  }
  const groupAverageBySkill = Object.entries(skillTotals)
    .map(([skill, values]) => ({ skill, media: Math.round(values.reduce((a, b) => a + b, 0) / values.length), amostra: values.length }))
    .sort((a, b) => a.media - b.media);
  const groupAverage = reports.length ? Math.round(reports.reduce((sum, r) => sum + r.summary.currentAverage, 0) / reports.length) : 0;
  return {
    nome,
    geradoEm: new Date().toISOString(),
    totalAlunos: reports.length,
    groupAverage,
    groupAverageBySkill,
    alertingCount: reports.filter((r) => r.alerts.some((a) => a.severidade !== 'baixa')).length,
    pendingConsent: reports.filter((r) => !r.consent).length,
    reports,
  };
}

/**
 * Cria a configuração reutilizável de um bloco automático (nome, critério e
 * lista de alunos), persistida em `data.reportBlocks` e exportável/importável
 * como JSON para reaproveitar a mesma seleção em ciclos futuros.
 */
export function createReportBlockConfig({ nome, studentIds = [], criterio = 'selecionados' } = {}) {
  return { id: generateId('bloco'), nome: nome || 'Bloco de relatórios', criterio, studentIds, criadoEm: todayISO() };
}

export function serializeReportBlockConfig(config) {
  return JSON.stringify({ tipo: 'sgaee_bloco_relatorio', versao: 1, ...config }, null, 2);
}

export function parseReportBlockConfig(json) {
  const parsed = JSON.parse(json);
  if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.studentIds)) {
    throw new Error('Arquivo de bloco de relatório inválido: esperado um objeto com "studentIds".');
  }
  return {
    id: parsed.id || generateId('bloco'),
    nome: parsed.nome || 'Bloco importado',
    criterio: parsed.criterio || 'selecionados',
    studentIds: parsed.studentIds.filter(Boolean),
    criadoEm: parsed.criadoEm || todayISO(),
  };
}
